import { Button, Popover, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { DatePicker, type DayMonthYear } from '@/components/date/datePicker';
import { SelectionPanel } from '@/components/panels/selectionPanel/selectionPanel';
import dayjs from '@/utils/dayjs';

import { getDayOfYearDisplay } from '../frequency';

export const DaysOfYearSelector = ({
  days,
  onChange,
  title,
}: {
  days: DayMonthYear[];
  onChange: (days: { day: number; month: number }[]) => void;
  title: string;
}) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  return (
    <>
      <Button
        sx={{
          padding: '5px 12px',
        }}
        ref={anchorRef}
        onClick={() => setPopoverOpen(true)}
        data-testid='days-of-year-select-button'
        disableFocusRipple
      >
        <Typography fontWeight={600}>
          {days.map((x) => getDayOfYearDisplay(x.day, x.month)).join(', ')}
        </Typography>

        <i className='material-symbols-arrow-drop-down-rounded text-[22px] ml-[4px]' />
      </Button>
      <DaysOfYearSelectorPopover
        title={title}
        anchorRef={anchorRef}
        days={days}
        onChange={(value) => {
          setPopoverOpen(false);
          onChange(value);
        }}
        popoverOpen={popoverOpen}
      />
    </>
  );
};

const DaysOfYearSelectorPopover = ({
  title,
  days,
  onChange,
  anchorRef,
  popoverOpen,
}: {
  title: string;
  popoverOpen: boolean;
  days: DayMonthYear[];
  onChange: (days: DayMonthYear[]) => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
}) => {
  const [value, setValue] = useState(days);

  useEffect(() => {
    setValue(days);
  }, [days]);

  return (
    <Popover
      disablePortal
      open={popoverOpen}
      anchorEl={anchorRef.current}
      onClose={() =>
        onChange(
          value.length
            ? value.sort((a, b) => a.month - b.month || a.day - b.day)
            : [{ day: 1, month: 1, year: dayjs.utc().year() }]
        )
      }
      sx={{
        '& .MuiPaper-root': {
          boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.20)',
        },
      }}
    >
      <SelectionPanel
        isLoading={false}
        drawerProps={{
          items: value,
          entityType: 'date',
          onRemove: (item) =>
            setValue((state) =>
              state.filter(
                (x) => !(x.day === item.day && x.month === item.month)
              )
            ),
          renderItem: (item) => (
            <Typography>{getDayOfYearDisplay(item.day, item.month)}</Typography>
          ),
          itemKey: (item) => `${item.day}-${item.month}`,
          clearAll: () => setValue([]),
        }}
      >
        <Stack
          className='px-[20px] pt-[24px] mb-[12px] overflow-auto'
          flexGrow={1}
        >
          <Typography variant='subtitle1' className='pl-[8px]'>
            {title}
          </Typography>
          <DatePicker value={value} setValue={setValue} isMultiSelect={true} />
        </Stack>
      </SelectionPanel>
    </Popover>
  );
};
